import { Localization } from "OW.Remoting/Remoting/Localization";
import { Control } from "UI/UI/UI/Controls/Control";
import { OrderType } from "../../../../Global/Base/DataSources/Interfaces";
import { DIContainer } from "../../../../Global/Base/DIContainer";
import { IDisposable } from "../../../../Global/Base/IDisposable";
import { StringUtils } from "../../../../Global/Base/Utils/StringUtils";
import { ModelDescriptionsManager } from "../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { IRole, IRoles } from "../../../../OW.Remoting/Remoting/IRolesAndICompetences";
import { IUserSettingsManager } from "../../../../OW.Remoting/Remoting/IUserSettingsManager";
import { Column } from "../../../UI.Controls/DataGridSubcontrols/Columns/Column";
import { ColumnFactory } from "../../../UI.Controls/DataGridSubcontrols/Columns/ColumnFactory";
import { ArrayDataSource, ArrayMSLOVDataSource } from "../../../UI.Controls/DataSource/ArrayDataSource";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { Caption } from "../../UI/Controls/Caption";
import { ControlParams, IControl } from "../../UI/Controls/ControlInterfaces";
import { UIManager } from "../../UI/UIManager";
import { FrameSharedMethods } from "../../UI/XGControls/Frames/FrameSharedMethods";
import { MultiSelectList } from "./MultiSelectList";

export class RolesRangesSettingsContainer extends Control implements IDisposable, IControl {
    private _okButton: HTMLElement;
    private _rolesDropdown: HTMLElement;

    private _rolesSectionEl: HTMLElement;
    private _competencesSectionEl: HTMLElement;
    private _selectedRoleId: string;
    private _rolesSection: MultiSelectList;
    private _competencesSection: MultiSelectList;

    private _userSettingsManager: IUserSettingsManager;

    constructor(params: ControlParams) {
        super(params);
        Caption.createCaptionsForElements(this.element); // vytvoří label elementy ke komponentám, obdobně jak to dělá AlignPanel
        this._userSettingsManager = DIContainer.instance.create(IUserSettingsManager);
        this._buildSections();
        this._registerListeners();
        this._registerSections();
    }

    private _registerListeners(): void {
        this._okButton = this.parentElement.querySelector("[data-id='ok-button-roles']");
        if (this._okButton) {
            this._okButton.classList.add("Disabled");
            this._events.addListener(this._okButton, "click", async () => {
                if (this._okButton.classList.contains("Disabled"))
                    return;

                UIManager.instance.showLoader();
                try {
                    if (!(await this.save()))
                        return;
                    this._okButton.classList.add("Disabled");
                } finally {
                    UIManager.instance.hideLoader();
                }
            });
        }
    }

    private _buildSections(): void {
        /*
        RegisterSection  v okně uživatelských nastavení pracuje s existující HTML stránkou (application-settings.html), která má elementy jako "#section-container" předdefinované
        a jen je oživuje. Pro custom komponentu je potřeba programově vytvořit. Metoda vezme `this.element` - kořenový element téhle komponenty
        a přes appendChild si vytvoří html strukturu, kterou pak může registersections oživuje.
        */
        const settingContainer = createElement({ classes: "roles-ranges-settings-container" });
        const wrp = createElement({ classes: "wrapper" });

        const rolesSection = createElement({ type: "section", html: "<h3><span class=\"icon user-list\" > </span><span class=\"loc-loading\" data-lkd=\"GUI-325062\" data-lkta=\"tc\"></span > </h3>" });
        this._rolesSectionEl = createElement({ id: "roles-container", classes: "dataGrid", attrs: { "data-objectname": "CRole", "data-savemethod": "Nastav", "data-displayedclass": "CRole", "data-npattrname": "np_Vybrane_role", "data-sort": "Role_uzivatelem.Fyzicke_jmeno" } });
        this._rolesSectionEl.appendChild(createElement({ classes: "errors" }));
        const rolesDatagridSpan = createElement({ type: "span" });
        rolesDatagridSpan.appendChild(createElement({ type: "span", id: "role-name-attr", attrs: { "data-tscitem": "Attribute", "data-width": "270", "data-lockey": "XG-42703", "data-attrname": "Nazev_role", "data-behaviour": "column" } }));
        rolesDatagridSpan.appendChild(createElement({ type: "span", id: "role-code-attr", attrs: { "data-tscitem": "Attribute", "data-width": "160", "data-lockey": "XG-42706", "data-attrname": "Fyzicke_jmeno", "data-behaviour": "column" } }));
        this._rolesSectionEl.appendChild(rolesDatagridSpan);
        this._rolesSectionEl.appendChild(createElement({ type: "tsc-mslovcheckeddatagrid" }));
        rolesSection.firstElementChild.id = UIManager.instance.generateElementID();
        this._rolesSectionEl.setAttribute("aria-labelledby", `${rolesSection.firstElementChild.id}`);
        rolesSection.appendChild(this._rolesSectionEl);
        wrp.appendChild(rolesSection);

        const compSection = createElement({ type: "section", html: "<h3><span class=\"icon settings-hotspot\" > </span><span class=\"loc-loading\" data-lkd=\"GUI-648804\" data-lkta=\"tc\"></span > </h3>" });
        compSection.appendChild(createElement({ type: "select", id: "competence-role", classes: "GenericControl Enabled EditableControl Edit" }));
        Localization.addLocData(compSection.children[1] as HTMLElement, "GUI-799278", ["title", "ariaLabel"]); // Role pro výběr okruhů
        this._competencesSectionEl = createElement({ id: "competences-container", classes: "dataGrid", attrs: { "data-objectname": "CKompetence", "data-savemethod": "Nastav", "data-displayedclass": "CKompetence", "data-npattrname": "Vybrane_okruhy", "data-condition": "Komp_uzivatele = @0 and Prideleno = true", "data-conditionparams": "Nova_Role", "data-sort": "Komp_okruh.Nazev" } });
        this._competencesSectionEl.appendChild(createElement({ classes: "errors" }));
        const competencesDatagridSpan = createElement({ type: "span" });
        competencesDatagridSpan.appendChild(createElement({ type: "span", id: "comp-name-attr", attrs: { "data-tscitem": "Attribute", "data-width": "270", "data-lockey": "XG-42583", "data-attrname": "Komp_okruh.Nazev", "data-behaviour": "column" } }));
        competencesDatagridSpan.appendChild(createElement({ type: "span", id: "comp-code-attr", attrs: { "data-tscitem": "Attribute", "data-width": "100", "data-lockey": "XG-42586", "data-attrname": "Komp_okruh.Kod", "data-behaviour": "column" } }));
        this._competencesSectionEl.appendChild(competencesDatagridSpan);
        this._competencesSectionEl.appendChild(createElement({ type: "tsc-mslovcheckeddatagrid" }));
        compSection.firstElementChild.id = UIManager.instance.generateElementID();
        this._competencesSectionEl.setAttribute("aria-labelledby", `${compSection.firstElementChild.id}`);
        compSection.appendChild(this._competencesSectionEl);
        wrp.appendChild(compSection);

        const btn = createElement({ classes: "bottom-buttons" });
        btn.appendChild(createElement({ type: "button", classes: "primary btn btn-large loc-loading", attrs: { "data-id": "ok-button-roles", "data-lkd": "GUI-781276", "data-lkta": "tc" } })); //Změnit role
        wrp.appendChild(btn);

        settingContainer.appendChild(wrp);

        this.element.appendChild(settingContainer);
    }

    /**
     * Pro každou oblast stránky s vlastním objektem je nadefinován SectionHandler, který ji obsluhuje
     */
    private async _registerSections(): Promise<void> {
        UIManager.instance.showLoader();
        try {
            const userProfileRoles = await this._userSettingsManager.getRoles();
            const userRoles: IRole[] = Object.values(userProfileRoles);
            const userProfileRolesSorted = this._sortSettingItemsInfo(userRoles);
            const rolesSectionDataSource = this._createDataSource(userProfileRolesSorted);

            this._rolesSection = new MultiSelectList(
                this._rolesSectionEl, rolesSectionDataSource, this._rolesSectionEl.getAttribute("data-npattrname"),
                await this._createColumns(this._rolesSectionEl.getAttribute("data-displayedclass"), this._rolesSectionEl as HTMLElement),
                [{ attributeName: this._rolesSectionEl.getAttribute("data-sort"), order: OrderType.ascending }],
            );
            this._events.add(this._rolesSection.selectionChanged, (_sender, prm) => {
                this._userSettingsManager.updateRoleActivity(prm.id, prm.value);
                this._okButton.classList.toggle("Disabled", false);
            });

            this._rolesDropdown = this._createRolesDropdown(this.element.querySelector("#competence-role"), userProfileRoles);
            const nameAttr = this.element.querySelector("#comp-name-attr")?.getAttribute("data-attrname");
            const codeAttr = this.element.querySelector("#comp-code-attr")?.getAttribute("data-attrname");

            this._events.addListener(this._rolesDropdown, "change", async (event: any) => {
                this._selectedRoleId = event.target.value;
                this._competencesSection.dataSource.init(this._sortSettingItemsInfo(userProfileRoles[event.target.value]?.competenceRanges), "id", [{ originalName: "name", newName: nameAttr }, { originalName: "code", newName: codeAttr }]);
                await this._competencesSection.dataSource.loadNewData(false);
            });

            const competencesSectionDataSource = new ArrayMSLOVDataSource();

            this._selectedRoleId = (this._rolesDropdown as HTMLSelectElement).value;
            competencesSectionDataSource.init(this._sortSettingItemsInfo(userProfileRoles[this._selectedRoleId]?.competenceRanges), "id", [{ originalName: "name", newName: nameAttr }, { originalName: "code", newName: codeAttr }]);

            this._competencesSection = new MultiSelectList(
                this._competencesSectionEl, competencesSectionDataSource, this._competencesSectionEl.getAttribute("data-npattrname"),
                await this._createColumns(this._competencesSectionEl.getAttribute("data-displayedclass"), this._competencesSectionEl as HTMLElement),
                [{ attributeName: this._competencesSectionEl.getAttribute("data-sort"), order: OrderType.ascending }],
            );

            this._events.add(this._competencesSection.selectionChanged, (_sender, prm) => {
                this._userSettingsManager.updateCompetenceActivity(this._selectedRoleId, prm.id, prm.value);
                this._okButton.classList.toggle("Disabled", false);
            });
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    private _createRolesDropdown = (rootElement: HTMLElement, roles: IRoles): HTMLElement => {
        for (const role of Object.values(roles).sort((a, b) => (a.name.localeCompare(b.name)))) {
            const option = createElement({ type: "option", value: role.id, html: StringUtils.stringToHTMLText(role.name) });
            rootElement.appendChild(option);
        }
        return rootElement;
    };

    private async _createColumns(objectName: string, element: HTMLElement): Promise<Column[]> {
        const columnsDefinition = FrameSharedMethods.getColumnsDefinition(element);
        const attrNames = columnsDefinition.map((column) => column.attrName);
        const attrInfo = await ModelDescriptionsManager.instance.getAttributesInfo(objectName, attrNames);

        const roleColumns: Column[] = [];
        for (const colDef of columnsDefinition) {
            const ai = attrInfo.find(i => StringUtils.compareCaseInsensitive(i.attributeName, colDef.attrName));
            const col = ColumnFactory.initColumn(colDef, ai.type, ai);
            roleColumns.push(col);
        }

        return roleColumns;
    }

    public async save(): Promise<boolean> {
        return (await Promise.all([this._userSettingsManager.saveData().then(() => true)])).every(succeeded => succeeded);
    }

    /**
     * Vytvoření datového zdroje pro sekci uživatelských rolí.
     * @param roles Vstupní data; seznam přidělených rolí.
     */
    protected _createDataSource<T extends object>(roles: IRole[]): ArrayDataSource<T> {
        const dataSource = new ArrayMSLOVDataSource();
        const nameAttr = this.element.querySelector("#role-name-attr")?.getAttribute("data-attrname");
        const codeAttr = this.element.querySelector("#role-code-attr")?.getAttribute("data-attrname");
        dataSource.init(roles, "id", [{ originalName: "name", newName: nameAttr }, { originalName: "code", newName: codeAttr }, { originalName: "implicit", newName: "disabled" }]);
        return dataSource as ArrayDataSource<T>;
    }

    /**
     * Funkce vrací HTML prvek reprezentující tuto tříu.
     **/
    protected _getMainContainerElement(): HTMLElement {
        return document.querySelector("RoleOkruhySettings");
    }

    /**
     * Funkce setřídí položky IUserRole nejprve podle aktivnosti a poté podle názvu. Pokud je zadáno undefined, vrací undefined.
     * @param unsortedItems Nesetříděné položky IUserRole.
     */
    protected _sortSettingItemsInfo(unsortedItems: IRole[] | undefined): IRole[] | undefined {
        if (unsortedItems) {
            return unsortedItems.sort((a, b) => (a.active === b.active) ? a.name.localeCompare(b.name) : (a.active < b.active) ? 1 : -1);
        } else {
            return undefined;
        }
    }
}
